/**
 * Tipos do componente `flux.list@1` — espelham o contrato de `@flux/iara-core`
 * (`components/manifests/flux-list-v1.ts`), mantidos no client por desacoplamento
 * (mesmo critério do `chat.model.ts`; evita puxar `node:crypto` do iara-core).
 *
 * O `ChatComponent.data`/`.presentation` chegam como `unknown` (JSON do backend);
 * os guards fazem o narrowing seguro antes de renderizar — payload inesperado
 * vira `null` e o renderer cai no `fallbackText`.
 */

export interface FluxListItem {
  id: string;
  title: string;
  subtitle?: string;
  entityType: string;
  entityData: Record<string, unknown>;
  /** URL canônica para abrir a entidade no app (linha vira link em nova aba). */
  canonicalUrl?: string;
}

export interface FluxListPagination {
  totalCount?: number;
  pageSize: number;
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface FluxListData {
  title: string;
  items: FluxListItem[];
  pagination: FluxListPagination;
}

export type FluxListBadgeTheme = 'blue' | 'default' | 'green' | 'magenta' | 'orange' | 'red';

export interface FluxListBadge {
  itemId: string;
  label: string;
  theme: FluxListBadgeTheme;
}

export interface FluxListAvatar {
  initials: string;
  color?: string;
  name: string;
}

export interface FluxListPresentation {
  density: 'compact' | 'comfortable';
  showSubtitle: boolean;
  badges: FluxListBadge[];
  avatars: Record<string, FluxListAvatar[]>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Narrowing de `flux.list@1` data. Valida o shape mínimo necessário para
 * renderizar (title + items[] com id/title + pagination.pageSize). Retorna
 * `null` em qualquer divergência para o renderer cair no fallback.
 */
export function asFluxListData(data: unknown): FluxListData | null {
  if (!isRecord(data)) return null;
  if (!isString(data['title']) || data['title'].length === 0) return null;
  if (!Array.isArray(data['items'])) return null;
  if (!isRecord(data['pagination'])) return null;
  if (typeof data['pagination']['pageSize'] !== 'number') return null;

  const items = data['items'].every(
    (item) => isRecord(item) && isString(item['id']) && isString(item['title']),
  );
  if (!items) return null;

  return data as unknown as FluxListData;
}

const BADGE_THEMES = new Set<FluxListBadgeTheme>([
  'blue',
  'default',
  'green',
  'magenta',
  'orange',
  'red',
]);

/**
 * Narrowing de `flux.list@1` presentation. É opcional (o backend pode omitir),
 * então um payload inválido vira `null` e o renderer usa os defaults.
 */
export function asFluxListPresentation(presentation: unknown): FluxListPresentation | null {
  if (!isRecord(presentation)) return null;

  const badges = Array.isArray(presentation['badges'])
    ? presentation['badges'].filter(
        (badge): badge is FluxListBadge =>
          isRecord(badge) &&
          isString(badge['itemId']) &&
          isString(badge['label']) &&
          BADGE_THEMES.has(badge['theme'] as FluxListBadgeTheme),
      )
    : [];

  const avatars: Record<string, FluxListAvatar[]> = {};
  if (isRecord(presentation['avatars'])) {
    for (const [itemId, list] of Object.entries(presentation['avatars'])) {
      if (!Array.isArray(list)) continue;
      avatars[itemId] = list.filter(
        (avatar): avatar is FluxListAvatar =>
          isRecord(avatar) && isString(avatar['initials']) && isString(avatar['name']),
      );
    }
  }

  return {
    density: presentation['density'] === 'compact' ? 'compact' : 'comfortable',
    showSubtitle: presentation['showSubtitle'] !== false,
    badges,
    avatars,
  };
}
